import React, { useEffect, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import ConfirmDialog from "../components/ConfirmDialog";
import { Wallet } from "lucide-react";
import { toast } from "react-toastify";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "₹0.00";
  }

  return `₹${amount.toFixed(2)}`;
};

export default function DeliveryChargesSection({ userID }) {
  const {
    getDeliveryChargesAndDiscount,
    updateDeliveryCharges,
    deleteDeliveryCharges,
  } = useOrderSettings();

  const [loading, setLoading] = useState(false);
  const [chargesData, setChargesData] = useState([]);

  const [confirm, setConfirm] = useState({
    open: false,
    onConfirm: null,
    title: "",
    message: "",
  });

  const [form, setForm] = useState({
    charges: "",
    fromAmount: "",
    toAmount: "",
    chargeType: "",
  });

  const ask = (title, message, callback) => {
    setConfirm({
      open: true,
      title,
      message,
      onConfirm: async () => {
        setConfirm({
          open: false,
          onConfirm: null,
          title: "",
          message: "",
        });

        await callback();
      },
    });
  };

  const cancelConfirm = () => {
    setConfirm({
      open: false,
      onConfirm: null,
      title: "",
      message: "",
    });
  };

  const load = async () => {
    if (!userID) {
      setChargesData([]);
      return;
    }

    setLoading(true);

    try {
      const result = await getDeliveryChargesAndDiscount(userID);

      if (result?.success) {
        setChargesData(
          Array.isArray(result?.charges)
            ? result.charges
            : [],
        );
      } else {
        setChargesData([]);

        toast.error(
          result?.message ||
            "Failed to fetch delivery charges",
        );
      }
    } catch (error) {
      console.error("DELIVERY CHARGES LOAD ERROR:", error);

      setChargesData([]);
      toast.error("Failed to fetch delivery charges");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    const {
      charges,
      fromAmount,
      toAmount,
      chargeType,
    } = form;

    if (!userID) {
      toast.error("Please select a client");
      return;
    }

    if (
      !charges ||
      !fromAmount ||
      !toAmount ||
      !chargeType
    ) {
      toast.error("Please fill all fields");
      return;
    }

    const chargesValue = Number(charges);
    const fromAmountValue = Number(fromAmount);
    const toAmountValue = Number(toAmount);

    if (
      !Number.isFinite(chargesValue) ||
      chargesValue < 0
    ) {
      toast.error("Enter valid delivery charges");
      return;
    }

    if (
      !Number.isFinite(fromAmountValue) ||
      fromAmountValue < 0
    ) {
      toast.error("Enter a valid from amount");
      return;
    }

    if (
      !Number.isFinite(toAmountValue) ||
      toAmountValue <= 0
    ) {
      toast.error("Enter a valid to amount");
      return;
    }

    if (toAmountValue < fromAmountValue) {
      toast.error("To amount cannot be less than from amount");
      return;
    }

    if (
      chargeType === "percentage" &&
      chargesValue > 100
    ) {
      toast.error("Percentage charges cannot exceed 100%");
      return;
    }

    setLoading(true);

    try {
      const result = await updateDeliveryCharges({
        userid: userID,
        charges: String(chargesValue),
        fromAmount: String(fromAmountValue),
        toAmount: String(toAmountValue),
        chargeType,
      });

      if (result?.success) {
        toast.success(
          result?.message ||
            "Delivery charges updated successfully",
        );

        setForm({
          charges: "",
          fromAmount: "",
          toAmount: "",
          chargeType: "",
        });

        await load();
      } else {
        toast.error(
          result?.message ||
            "Failed to update delivery charges",
        );
      }
    } catch (error) {
      console.error("DELIVERY CHARGES SAVE ERROR:", error);
      toast.error("Failed to update delivery charges");
    } finally {
      setLoading(false);
    }
  };

  const removeRow = (chargeId) => {
    if (!userID) {
      toast.error("Please select a client");
      return;
    }

    ask(
      "Remove delivery charge?",
      "This action cannot be undone.",
      async () => {
        setLoading(true);

        try {
          const result = await deleteDeliveryCharges({
            userid: userID,
            chargeId,
          });

          if (result?.success) {
            toast.success(
              result?.message ||
                "Delivery charge removed successfully",
            );

            await load();
          } else {
            toast.error(
              result?.message ||
                "Failed to remove delivery charge",
            );
          }
        } catch (error) {
          console.error(
            "DELETE DELIVERY CHARGE ERROR:",
            error,
          );

          toast.error("Failed to remove delivery charge");
        } finally {
          setLoading(false);
        }
      },
    );
  };

  useEffect(() => {
    load();
  }, [userID]);

  return (
    <>
      {loading && chargesData.length === 0 && (
        <SectionCard
          icon={Wallet}
          title="Currently Applied Delivery Charges"
        >
          <div className="flex items-center justify-center py-8 text-sm text-slate-500">
            Loading delivery charges...
          </div>
        </SectionCard>
      )}

      {!loading && userID && chargesData.length === 0 && (
        <SectionCard
          icon={Wallet}
          title="Currently Applied Delivery Charges"
        >
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
            <p className="text-sm font-medium text-slate-700">
              No delivery charges configured
            </p>

            <p className="mt-1 text-xs text-slate-500">
              Add delivery charges using the form below.
            </p>
          </div>
        </SectionCard>
      )}

      {chargesData.map((chargeItem) => (
        <SectionCard
          key={chargeItem.id}
          icon={Wallet}
          title="Currently Applied Delivery Charges"
          right={
            <button
              type="button"
              onClick={() => removeRow(chargeItem.id)}
              disabled={loading}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Remove
            </button>
          }
        >
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div>
              <Label>Charges</Label>

              <div className="text-sm font-medium text-slate-800">
                {chargeItem.charge_type === "fixed"
                  ? formatCurrency(chargeItem.charges)
                  : `${Number(
                      chargeItem.charges || 0,
                    ).toFixed(2)}%`}
              </div>
            </div>

            <div>
              <Label>Type</Label>

              <div className="text-sm font-medium capitalize text-slate-800">
                {chargeItem.charge_type || "—"}
              </div>
            </div>

            <div>
              <Label>From</Label>

              <div className="text-sm font-medium text-slate-800">
                {formatCurrency(chargeItem.from_amt)}
              </div>
            </div>

            <div>
              <Label>To</Label>

              <div className="text-sm font-medium text-slate-800">
                {formatCurrency(chargeItem.to_amt)}
              </div>
            </div>

            <div>
              <Label>GST</Label>

              <div className="text-sm font-medium text-slate-800">
                {Number(chargeItem.gst || 0).toFixed(2)}%
              </div>
            </div>
          </div>
        </SectionCard>
      ))}

      <SectionCard
        icon={Wallet}
        title="Update Delivery Charges"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <Label>Charges</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.charges}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  charges: event.target.value,
                }))
              }
              placeholder="30"
            />
          </div>

          <div>
            <Label>From Amount</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.fromAmount}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  fromAmount: event.target.value,
                }))
              }
              placeholder="1"
            />
          </div>

          <div>
            <Label>To Amount</Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.toAmount}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  toAmount: event.target.value,
                }))
              }
              placeholder="299"
            />
          </div>

          <div>
            <Label>Charge Type</Label>

            <Select
              value={form.chargeType}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  chargeType: event.target.value,
                }))
              }
            >
              <option value="">Select</option>
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </Select>
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={save}
            disabled={loading || !userID}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Delivery Charges"}
          </button>
        </div>
      </SectionCard>

      <ConfirmDialog
        {...confirm}
        onCancel={cancelConfirm}
      />
    </>
  );
}